import { useCallback } from 'react';
import { useStore } from 'zustand';
import type { DesktopShellStore, StateSetter } from '@/lib/desktop-shell/store';

type PendingDraft = { value: string; timer: ReturnType<typeof setTimeout> };
const pendingDrafts = new WeakMap<DesktopShellStore, Map<string, PendingDraft>>();

function pendingFor(store: DesktopShellStore): Map<string, PendingDraft> {
  const existing = pendingDrafts.get(store);
  if (existing !== undefined) return existing;
  const created = new Map<string, PendingDraft>();
  pendingDrafts.set(store, created);
  return created;
}

/** Publishes queued editor input before a direct draft read or write. */
export function flushPendingComposerDraft(store: DesktopShellStore, chatId: string): void {
  const drafts = pendingDrafts.get(store);
  const pending = drafts?.get(chatId);
  if (pending === undefined) return;
  clearTimeout(pending.timer);
  drafts?.delete(chatId);
  store
    .getState()
    .setPaneDrafts((current) =>
      (current[chatId] ?? '') === pending.value ? current : { ...current, [chatId]: pending.value },
    );
}

/** Exposes the active pane's draft while preserving drafts for every conversation. */
export function useComposerDraft(options: {
  store: DesktopShellStore;
  chatId: string;
}): [string, StateSetter<string>] {
  const { store, chatId } = options;
  const text = useStore(store, (state) => state.paneDrafts[chatId] ?? '');
  const setPaneDrafts = useStore(store, (state) => state.setPaneDrafts);
  const setText = useCallback<StateSetter<string>>(
    (value) => {
      const drafts = pendingFor(store);
      const previous = drafts.get(chatId)?.value ?? store.getState().paneDrafts[chatId] ?? '';
      const next = typeof value === 'function' ? value(previous) : value;
      if (previous === next) return;
      const queued = drafts.get(chatId);
      if (queued !== undefined) {
        queued.value = next;
        return;
      }
      // Native paste and accessibility typing can make Tiptap emit dozens of
      // updates in one browser task. Publish one coalesced store update on the
      // next task so React never receives a recursively nested update storm.
      const pending: PendingDraft = {
        value: next,
        timer: setTimeout(() => {
          if (drafts.get(chatId) !== pending) return;
          drafts.delete(chatId);
          setPaneDrafts((current) =>
            (current[chatId] ?? '') === pending.value
              ? current
              : { ...current, [chatId]: pending.value },
          );
        }, 0),
      };
      drafts.set(chatId, pending);
    },
    [chatId, setPaneDrafts, store],
  );

  // The editor reads its own text back: typing not published yet is still the draft, so a
  // render in between (a busy conversation re-renders often) never sets older text back.
  return [pendingFor(store).get(chatId)?.value ?? text, setText];
}
